import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, AlertCircle, CheckCircle, Sparkles, Coffee, Utensils } from 'lucide-react';

const LoginScreen = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [panelType, setPanelType] = useState('admin'); // 'admin' or 'orders'

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Lütfen tüm alanları doldurun');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await login(formData.username, formData.password);
      setSuccess('Giriş başarılı! Yönlendiriliyorsunuz...');
      
      // Redirect based on selected panel type
      setTimeout(() => {
        if (panelType === 'orders') {
          navigate('/orders');
        } else {
          navigate('/');
        }
      }, 1500);
    } catch (error) {
      setError(error.message || 'Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Restaurant Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
          alt="Restaurant Background" 
          className="w-full h-full object-cover filter blur-sm opacity-30"
        />
      </div>
      
      {/* Overlay for better readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/60 via-blue-950/50 to-indigo-950/60"></div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-gradient-to-br from-slate-600 to-blue-700 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob animation-delay-4000"></div>
      </div>

                    {/* Floating Icons */}
        <div className="absolute top-20 left-20 animate-bounce">
          <Coffee className="w-8 h-8 text-blue-300 opacity-80" />
        </div>
        <div className="absolute top-32 right-32 animate-bounce animation-delay-1000">
          <Utensils className="w-6 h-6 text-indigo-300 opacity-80" />
        </div>
        <div className="absolute bottom-32 left-32 animate-bounce animation-delay-2000">
          <Sparkles className="w-7 h-7 text-slate-300 opacity-80" />
        </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
                 <div className={`max-w-sm w-full transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        {/* Logo/Brand */}
           <div className="text-center mb-6">
                          <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-700 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-xl transform hover:scale-105 transition-transform duration-300">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-2 h-2 text-white" />
                </div>
          </div>
                           <h1 className="text-2xl font-black bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent mb-2 drop-shadow-sm">
                 Hoş Geldiniz
               </h1>
               <p className="text-gray-200 text-sm font-semibold">Digital Menu System'e giriş yapın</p>
        </div>

        {/* Login Form */}
           <div className="bg-white/50 backdrop-blur-3xl rounded-2xl shadow-2xl border border-white/10 p-6 transform hover:scale-[1.02] transition-transform duration-300">
                         <form onSubmit={handleSubmit} className="space-y-4">
            {/* Panel Type Selection */}
            <div className="group">
              <label className="block text-base font-bold text-gray-800 mb-3">
                Panel Seçimi
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="panelType"
                    value="admin"
                    checked={panelType === 'admin'}
                    onChange={(e) => setPanelType(e.target.value)}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500 focus:ring-2"
                    disabled={loading}
                  />
                  <span className="text-base font-semibold text-gray-800">Admin Paneli</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="panelType"
                    value="orders"
                    checked={panelType === 'orders'}
                    onChange={(e) => setPanelType(e.target.value)}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500 focus:ring-2"
                    disabled={loading}
                  />
                  <span className="text-base font-semibold text-gray-800">Sipariş Paneli</span>
                </label>
              </div>
            </div>

            {/* Username Field */}
               <div className="group">
                 <label htmlFor="username" className="block text-xs font-bold text-gray-800 mb-2">
                Kullanıcı Adı
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                     <User className="h-5 w-5 text-gray-700 group-focus-within:text-blue-700 transition-colors" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                                          className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 bg-white/20 backdrop-blur-lg text-sm"
                  placeholder="Kullanıcı adınızı girin"
                      style={{ color: '#1f2937' }}
                  disabled={loading}
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password Field */}
               <div className="group">
                 <label htmlFor="password" className="block text-xs font-bold text-gray-800 mb-2">
                Şifre
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                     <Lock className="h-5 w-5 text-gray-700 group-focus-within:text-blue-700 transition-colors" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                     className="block w-full pl-10 pr-10 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 bg-white/20 backdrop-blur-lg text-sm"
                  placeholder="Şifrenizi girin"
                      style={{ color: '#1f2937' }}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                         className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-blue-700 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                           <EyeOff className="h-5 w-5 text-gray-700" />
                  ) : (
                           <Eye className="h-5 w-5 text-gray-700" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
                 <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg animate-pulse">
                   <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                      <span className="text-xs text-red-800 font-bold">{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                   <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                      <span className="text-xs text-green-800 font-bold">{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
                 className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold text-sm hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Giriş Yapılıyor...
                </div>
              ) : (
                'Giriş Yap'
              )}
            </button>
          </form>

          {/* Help Links */}
             <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="text-center">
                                  <a href="#" className="text-xs text-blue-700 hover:text-blue-800 font-bold transition-colors duration-200 hover:underline">
                Şifremi Unuttum
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
                        <p className="text-sm text-gray-300 font-medium">
            © 2024 Digital Menu System. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
};

export default LoginScreen;
